import { useState, type KeyboardEvent } from 'react';
import { Color, FieldSize, Size, noteFromSurface, noteIsBlank, noteTextToStore, noteWords, richTextFromPlain, noteIsRich, sanitizeNoteText, t } from '@bms2/shared';
import { Button, RichTextEditor, Textarea, ToggleSwitch } from '../controls';
import styles from './NoteComposer.module.css';

export interface NoteComposerProps {
  /**
   * Writes the note through the route the list already writes by. Answers true
   * once the server has taken it and false where it refused or could not be
   * reached; the caller says why beside the page, as it did for the modal.
   */
  onAdd: (text: string) => Promise<boolean>;
  /** The box's name, which a test and a browser's own form memory read. */
  name: string;
  /** What the empty box prompts; the string table's own unless a list asks a question of its own. */
  placeholder?: string;
  /** The box's name for a screen reader; the notes list's own unless the box writes something else, as an article's comment does (decision 9580). */
  label?: string;
  /** Why Add rests disabled on an empty box; the notes list's own sentence unless the box writes something else, as a comment does (T3449). */
  emptyReason?: string;
}

/**
 * 🔒 The one inline composer every notes list opens with (piece 13; decision
 * 0910): a box a single line tall that grows as it is typed into, with Add at
 * its right. Add, or Ctrl+Enter (Cmd+Enter on a Mac) inside the box, writes the
 * note; the box empties only once the server has taken it.
 *
 * 🔨 A refused or unreachable save leaves the typed text in the box, so a
 * second press sends it again and nothing typed is lost to a slow network.
 * Text typed while the save was in flight is not the text sent, so it stays too.
 *
 * 🔨 Add is disabled until the box holds text, and says so where it is rested
 * on (decision 0559). An empty note is refused here and by the server alike.
 *
 * 🔒 The box is the Rich Text Editor with its Rich text switch on (decision 7290,
 * amending 7180), a person turning it off for a plain box whose paste keeps the
 * words only. Each note opens on it; nothing remembers the switch. A note typed
 * with no formatting is written as plain words.
 */
export function NoteComposer({ onAdd, name, placeholder, label = t('note.composer.label'), emptyReason = t('note.composer.empty') }: NoteComposerProps) {
  const [draft, setDraft] = useState('');
  const [rich, setRich] = useState(true);
  const [busy, setBusy] = useState(false);
  const empty = noteIsBlank(draft);
  const switchRich = (on: boolean) => {
    setRich(on);
    setDraft((d) => (on ? (noteIsRich(d) ? d : richTextFromPlain(d)) : noteWords(d)));
  };
  const submit = async () => {
    if (empty || busy) return;
    const sent = draft;
    setBusy(true);
    const held = await onAdd(rich ? noteTextToStore(noteFromSurface(sent)) : sent.trim());
    setBusy(false);
    if (held) setDraft((now) => (now === sent ? '' : now));
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return;
    event.preventDefault();
    void submit();
  };
  return (
    <div className={styles.composer} data-note-composer={name} data-note-composer-busy={busy ? 'true' : 'false'} data-note-composer-rich={rich ? 'on' : 'off'} onKeyDown={onKeyDown}>
      <span className={styles.richSwitch}>
        <span className={styles.richLabel}>{t('note.richText')}</span>
        <ToggleSwitch checked={rich} onChange={switchRich} name={`${t('note.richText')} ${name}`} />
      </span>
      <div className={styles.row}>
        <div className={styles.box}>
          {rich ? (
            // 🔒 The notes' text set and allowance, as the note window draws them (decision 7180).
            <RichTextEditor label={label} labelHidden value={draft} onChange={setDraft} textOnly sanitize={sanitizeNoteText} sanitizePaste={sanitizeNoteText} name={name} placeholder={placeholder ?? t('note.composer.placeholder')} />
          ) : (
            // 🔒 One line to start, growing with what is typed to the control's own cap, and the whole row wide.
            <Textarea size={FieldSize.Column} label={label} labelHidden value={draft} onChange={setDraft} name={name} placeholder={placeholder ?? t('note.composer.placeholder')} rows={1} />
          )}
        </div>
        <span className={styles.add} data-note-composer-add={name}>
          <Button size={Size.Small} color={Color.Navy} text={[t('note.composer.add')]} actions={[() => void submit()]} disabled={empty || busy} disabledReason={busy ? t('note.composer.saving') : emptyReason} />
        </span>
      </div>
    </div>
  );
}
